import { useState, useEffect, useRef } from 'react';
import { evaluateProximity, AddressLocation } from '@/lib/geoProximity';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, ChevronRight, ChevronLeft, Mail, Phone, MapPin, Loader2, AlertTriangle, ShieldAlert } from 'lucide-react';
import { Stepper, Step } from '@/components/ui/stepper';
import { apiService } from '@/services/api';

interface CaseData {
  code: string;
  applicantNo: string;
  name: string;
  fathersName: string;
  phone: string;
  formSubmitDate: string;
  presentAddress: string;
  address?: string;
  /**
   * Reference point for this case's address. When present the form may only be
   * opened and submitted from within `radiusMeters` of it. Absent on legacy
   * cases and any case with no pin, where the gate does not apply.
   */
  addressLocation?: AddressLocation;
}

const steps: Step[] = [
  { id: 1, title: 'Personal Information', description: 'Contact & Address Details' },
  { id: 2, title: 'Document Upload', description: 'Upload Required Documents' },
];

const AddressVerificationPage = () => {
  const { token } = useParams<{ token: string }>();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [caseData, setCaseData] = useState<CaseData | null>(null);
  type GeoStatus =
    | 'idle'
    | 'requesting'
    | 'granted'
    | 'denied_soft'
    | 'denied_hard'
    | 'timeout'
    | 'unavailable'
    | 'insecure_context'
    | 'unsupported';
  const [geolocationStatus, setGeolocationStatus] = useState<GeoStatus>('idle');

  const [formData, setFormData] = useState({
    // Contact Information
    contactPersonName: '',
    contactPersonRelation: '',
    numberOfFamilyMembers: '',
    contactPhoneNo: '',

    // ID Proof
    idProofType: '' as 'aadhaar' | 'pan' | 'voter_id' | 'passport' | 'driving_license' | 'other' | '',
    idProofNumber: '',

    // Address Details
    periodOfStay: '',
    differentAddress: '',
    residentialStatus: '' as 'owned' | 'rented' | 'company_provided' | 'pg' | 'other' | '',
    landmark: '',
    remarks: '',

    // Geolocation (will be captured automatically)
    latitude: undefined as number | undefined,
    longitude: undefined as number | undefined,
    gpsAccuracy: undefined as number | undefined,
  });

  // Per-file rejection messages, keyed by document field.
  const [fileErrors, setFileErrors] = useState<Record<string, string>>({});
  const [uploadProgress, setUploadProgress] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [documents, setDocuments] = useState({
    idProofOne: null as File | null,
    idProofTwo: null as File | null,
    houseImageOne: null as File | null,
    houseImageTwo: null as File | null,
    signature: null as File | null,
    selfie: null as File | null,
  });
  // docType -> the exact File already accepted by the server, so a retry after
  // a failed submit skips it instead of re-uploading all six.
  const uploadedRef = useRef<Record<string, File>>({});
  // field -> the most recent selection, so a slow read of an earlier pick
  // cannot overwrite a newer one.
  const latestPickRef = useRef<Record<string, File | null>>({});

  useEffect(() => {
    if (token) {
      fetchVerificationData();
      // Auto-attempt geolocation on mount. On iOS Safari / strict browsers
      // this may silently fail without prompting; in that case the UI falls
      // back to the 'idle' / error panels with a user-tap retry button.
      captureGeolocation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchVerificationData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getVerificationByToken(token!);

      if (response.success && response.data) {
        setCaseData(response.data);
      } else {
        setError(response.message || 'Invalid or expired verification link');
      }
    } catch (error: any) {
      console.error('Fetch error:', error);
      setError(error.message || 'Failed to load verification details');
    } finally {
      setLoading(false);
    }
  };

  const applyPosition = (position: GeolocationPosition) => {
    setFormData(prev => ({
      ...prev,
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      // Sent to the server so a borderline distance can be judged against the
      // quality of the fix rather than treated as exact.
      gpsAccuracy: position.coords.accuracy,
    }));
    setGeolocationStatus('granted');
  };

  const captureGeolocation = async () => {
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      setGeolocationStatus('insecure_context');
      return;
    }
    if (!('geolocation' in navigator)) {
      setGeolocationStatus('unsupported');
      return;
    }

    if (navigator.permissions && typeof navigator.permissions.query === 'function') {
      try {
        const perm = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        if (perm.state === 'denied') {
          setGeolocationStatus('denied_hard');
          return;
        }
      } catch {
        // Older Safari may throw on geolocation permission query — fall through.
      }
    }

    setGeolocationStatus('requesting');

    const tryGet = (highAccuracy: boolean, timeout: number) =>
      new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: highAccuracy,
          timeout,
          maximumAge: highAccuracy ? 0 : 60_000,
        });
      });

    try {
      const position = await tryGet(true, 25_000);
      applyPosition(position);
      return;
    } catch (e1: any) {
      console.warn('Geolocation phase 1 failed:', e1?.code, e1?.message);
      if (e1?.code === 1) {
        setGeolocationStatus('denied_soft');
        return;
      }
      try {
        const position = await tryGet(false, 15_000);
        applyPosition(position);
      } catch (e2: any) {
        console.warn('Geolocation phase 2 failed:', e2?.code, e2?.message);
        if (e2?.code === 1) setGeolocationStatus('denied_soft');
        else if (e2?.code === 2) setGeolocationStatus('unavailable');
        else if (e2?.code === 3) setGeolocationStatus('timeout');
        else setGeolocationStatus('timeout');
      }
    }
  };

  const getMobileOS = (): 'ios' | 'android' | 'windows' | 'mac' | 'other' => {
    if (typeof navigator === 'undefined') return 'other';
    const ua = navigator.userAgent || '';
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
    // iPadOS 13+ reports a desktop Safari UA; the touch-point count is the
    // documented way to tell an iPad from a Mac.
    if (/Macintosh/.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1) return 'ios';
    if (/Android/i.test(ua)) return 'android';
    if (/Windows/i.test(ua)) return 'windows';
    if (/Macintosh|Mac OS X/i.test(ua)) return 'mac';
    return 'other';
  };

  /**
   * Per-platform steps for re-enabling location after a hard block. Covers all
   * four target platforms: a candidate who denied permission must be able to
   * recover without leaving the page guessing.
   */
  const locationHelpSteps = (): { title: string; steps: string[] } => {
    switch (getMobileOS()) {
      case 'ios':
        return {
          title: 'To allow location on iPhone/iPad:',
          steps: [
            'Open the Settings app.',
            "Go to Privacy & Security → Location Services and make sure it's On.",
            'Scroll down, tap your browser (Safari Websites or Chrome), and choose Ask or Allow.',
            'Return to this page and tap Try Again.'
          ]
        };
      case 'android':
        return {
          title: 'To allow location on Android:',
          steps: [
            'Tap the lock icon (or ⓘ) in the address bar.',
            'Tap Permissions and set Location to Allow.',
            "Also ensure your phone's Location is on (Settings → Location).",
            'Return here and tap Try Again.'
          ]
        };
      case 'windows':
        return {
          title: 'To allow location on Windows:',
          steps: [
            'Click the lock icon (or ⓘ) at the left of the address bar.',
            'Set Location to Allow, then reload the page.',
            'If it stays blocked, open Windows Settings → Privacy & security → Location and turn on "Let apps access your location" and "Let desktop apps access your location".',
            'Return here and click Try Again.'
          ]
        };
      case 'mac':
        return {
          title: 'To allow location on Mac:',
          steps: [
            'Click the lock icon (or ⓘ) at the left of the address bar and set Location to Allow.',
            'In Safari you can also use Safari → Settings → Websites → Location and set this site to Allow.',
            'Open System Settings → Privacy & Security → Location Services and make sure it is on for your browser.',
            'Return here and click Try Again.'
          ]
        };
      default:
        return {
          title: 'To allow location:',
          steps: [
            "Open your browser's site settings for this page.",
            'Set Location to Allow.',
            "Make sure your device's location/GPS is turned on.",
            'Return here and tap Try Again.'
          ]
        };
    }
  };

  // --- Form-open proximity gate -----------------------------------------
  // The form is only rendered when the candidate is inside the case's radius.
  // Location permission is therefore mandatory: without a fix there is nothing
  // to measure, so the gate stays closed and shows the recovery instructions.
  //
  // Cases with no pinned reference are unaffected -- evaluateProximity returns
  // allowed for them -- so legacy links keep working exactly as before.
  const proximity = evaluateProximity(caseData?.addressLocation, {
    latitude: formData.latitude,
    longitude: formData.longitude,
    accuracyMeters: formData.gpsAccuracy,
  });

  const hasAddressPin = !!caseData?.addressLocation &&
    caseData.addressLocation.latitude != null &&
    caseData.addressLocation.longitude != null;

  // Gate applies only to pinned cases, and only once we know where we are.
  const blockedByProximity = hasAddressPin && !proximity.allowed;

  // While the very first fix is still being acquired we do not yet know whether
  // the candidate is inside the radius. Showing the red "Only on this location"
  // blocker during those seconds reads as a rejection to someone standing at
  // the correct address, so a neutral "checking" screen is shown instead.
  const awaitingFirstFix = hasAddressPin &&
    proximity.reason === 'no_captured_location' &&
    (geolocationStatus === 'idle' || geolocationStatus === 'requesting');

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Must match the multer limit in routes/addressVerification.js.
  const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;

  // Fields that must be captured with the camera rather than chosen from the
  // gallery. `capture` on the input drives the OS to open the camera directly;
  // this list is used for the labels and the freshness check below.
  const CAMERA_ONLY_FIELDS = ['houseImageOne', 'houseImageTwo', 'signature', 'selfie'];

  // A photo taken for this form is seconds old. A gallery image is typically
  // not -- so on a camera-only field a file whose lastModified is well in the
  // past indicates a gallery pick on a browser that ignored `capture`.
  // Deliberately generous (15 min) so a slow form fill is never penalised.
  const STALE_PHOTO_MS = 15 * 60 * 1000;

  const handleFileChange = async (field: keyof typeof documents, file: File | null) => {
    latestPickRef.current[field] = file;

    // Reject too-large files at selection time. Without this the file is
    // accepted with a green tick and only fails at submit, after the agent has
    // filled in the whole form.
    if (file && file.size > MAX_UPLOAD_BYTES) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
      setFileErrors(prev => ({
        ...prev,
        [field]: `This photo is ${sizeMb}MB — the limit is 15MB. Please retake it at a lower resolution or choose a smaller file.`
      }));
      setDocuments(prev => ({ ...prev, [field]: null }));
      return;
    }

    // Camera-only fields: reject an obviously pre-existing image. Desktop
    // browsers ignore `capture` entirely, so this is the only signal available
    // there; it is a deterrent, not a guarantee, and the value is advisory.
    if (file && CAMERA_ONLY_FIELDS.includes(field as string)) {
      const age = Date.now() - file.lastModified;
      if (Number.isFinite(age) && age > STALE_PHOTO_MS) {
        setFileErrors(prev => ({
          ...prev,
          [field]: 'Please take a new photo with your camera. Existing photos from your gallery are not accepted for this field.'
        }));
        setDocuments(prev => ({ ...prev, [field]: null }));
        return;
      }
    }

    // Copy the bytes into memory NOW. On Android a picked file is a content://
    // grant that is revoked once the browser is backgrounded — which every
    // camera field after this one does — and Drive/WhatsApp files can vanish.
    // The file was then unreadable at submit, fetch() rejected, and the agent
    // was told to "check your internet connection" on every retry, forever.
    let snapshot = file;
    if (file) {
      try {
        const bytes = await file.arrayBuffer();
        snapshot = new File([bytes], file.name, { type: file.type, lastModified: file.lastModified });
      } catch {
        if (latestPickRef.current[field] !== file) return;
        setFileErrors(prev => ({
          ...prev,
          [field]: 'This file could not be read from your phone. If it is in Google Drive, WhatsApp or another app, save it to your phone first, then choose it again.'
        }));
        setDocuments(prev => ({ ...prev, [field]: null }));
        return;
      }
      if (latestPickRef.current[field] !== file) return;
    }

    setFileErrors(prev => {
      const next = { ...prev };
      delete next[field as string];
      return next;
    });
    setDocuments(prev => ({ ...prev, [field]: snapshot }));
  };

  // Real-time validation for Step 1
  const isStep1Valid =
    formData.contactPersonName.trim() !== '' &&
    formData.contactPersonRelation.trim() !== '' &&
    formData.idProofType !== '' &&
    formData.residentialStatus !== '' &&
    geolocationStatus === 'granted';

  const validateStep1 = (): boolean => {
    if (!formData.contactPersonName.trim()) {
      alert('Contact Person Name is required');
      return false;
    }
    if (!formData.contactPersonRelation.trim()) {
      alert('Contact Person Relation is required');
      return false;
    }
    if (!formData.idProofType) {
      alert('ID Proof Type is required');
      return false;
    }
    if (!formData.residentialStatus) {
      alert('Residential Status is required');
      return false;
    }
    if (geolocationStatus !== 'granted') {
      alert('Please enable location access to proceed');
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!documents.idProofOne) {
      alert('ID Proof One is required');
      return false;
    }
    if (!documents.idProofTwo) {
      alert('ID Proof Two is required');
      return false;
    }
    if (!documents.houseImageOne) {
      alert('House Image One is required');
      return false;
    }
    if (!documents.houseImageTwo) {
      alert('House Image Two is required');
      return false;
    }
    if (!documents.signature) {
      alert('Signature is required');
      return false;
    }
    if (!documents.selfie) {
      alert('Selfie (Candidate Image) is required');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateStep2()) return;

    setSubmitting(true);

    try {
      // 1. Upload documents ONE AT A TIME.
      //
      // These were previously fired together via Promise.all. Six full-size
      // phone photos uploading simultaneously over mobile data routinely
      // exhausted the connection, and a single failure rejected the whole
      // batch with the browser's opaque "Failed to fetch" — after which every
      // upload had to be redone. Sequential is slower but survives weak
      // signal, and a failure names the exact file.
      const uploads: Array<{ file: File; docType: string; label: string }> = [
        { file: documents.idProofOne!, docType: 'id_proof_one', label: 'ID Proof One' },
        { file: documents.idProofTwo!, docType: 'id_proof_two', label: 'ID Proof Two' },
        { file: documents.houseImageOne!, docType: 'house_image_one', label: 'House Image One' },
        { file: documents.houseImageTwo!, docType: 'house_image_two', label: 'House Image Two' },
        { file: documents.signature!, docType: 'signature', label: 'Signature' },
        { file: documents.selfie!, docType: 'selfie', label: 'Selfie' },
      ];

      for (let i = 0; i < uploads.length; i++) {
        const { file, docType, label } = uploads[i];
        if (uploadedRef.current[docType] === file) continue;
        setUploadProgress(`Uploading ${label} (${i + 1} of ${uploads.length})…`);
        try {
          await apiService.uploadVerificationDocument(token!, file, docType);
          uploadedRef.current[docType] = file;
        } catch (uploadError: any) {
          throw new Error(`${label}: ${uploadError.message || 'upload failed'}`);
        }
      }

      setUploadProgress('Submitting verification…');

      // 2. Submit form data
      const submitData = {
        contactPersonName: formData.contactPersonName,
        contactPersonRelation: formData.contactPersonRelation,
        numberOfFamilyMembers: formData.numberOfFamilyMembers ? parseInt(formData.numberOfFamilyMembers) : undefined,
        contactPhoneNo: formData.contactPhoneNo,
        idProofType: formData.idProofType as 'aadhaar' | 'pan' | 'voter_id' | 'passport' | 'driving_license' | 'other',
        idProofNumber: formData.idProofNumber,
        periodOfStay: formData.periodOfStay,
        differentAddress: formData.differentAddress,
        residentialStatus: formData.residentialStatus as 'owned' | 'rented' | 'company_provided' | 'pg' | 'other',
        landmark: formData.landmark,
        remarks: formData.remarks,
        latitude: formData.latitude,
        longitude: formData.longitude,
        gpsAccuracy: formData.gpsAccuracy,
      };

      const submitResponse = await apiService.submitVerification(token!, submitData);

      if (!submitResponse.success) {
        const err: any = new Error(submitResponse.message || 'Failed to submit verification');
        // A proximity rejection is not retryable from where the candidate is
        // standing, so it must not carry the "tap Submit again" advice.
        err.isProximityBlock = Boolean((submitResponse as any).proximity);
        throw err;
      }

      setSubmitted(true);
    } catch (error: any) {
      console.error('Submit error:', error);
      // Uploads already completed are kept server-side, so a retry resumes
      // rather than starting over — say so, or the agent assumes total loss.
      setSubmitError(
        error.isProximityBlock
          ? `${error.message}\n\nYour details have not been lost. Please move to the address being verified and tap Submit again — already-uploaded files do not need to be re-selected.`
          : `${error.message || 'Failed to submit verification'}\n\nYour details have not been lost. Please tap Submit again — already-uploaded files do not need to be re-selected.`
      );
    } finally {
      setSubmitting(false);
      setUploadProgress('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-green-50 to-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-brand-green mx-auto mb-4"></div>
          <div className="text-lg text-gray-600">Loading verification details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-green-50 to-white">
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="pt-6 text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Verification Link Error</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-green-50 to-white">
        <Card className="w-full max-w-md shadow-xl">
          <CardContent className="pt-6 text-center">
            <div className="mb-6">
              <div className="w-20 h-20 bg-brand-green rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verification Submitted Successfully!</h2>
            <p className="text-gray-600 mb-6">
              Thank you for completing your address verification. We have received your submission and will review it shortly.
            </p>
            <div className="bg-brand-green-50 p-4 rounded-lg border border-brand-green-200">
              <p className="text-sm text-gray-700">
                A confirmation email will be sent to you shortly.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // --- Location gate: the form is not rendered until the candidate is at the
  // address. Shown for pinned cases only; legacy cases fall straight through.
  if (awaitingFirstFix) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-green-50 to-white flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-brand-green mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Checking your location…</h2>
          <p className="text-sm text-gray-600">
            If your device asks for permission, please choose <span className="font-semibold">Allow</span>.
          </p>
        </div>
      </div>
    );
  }

  if (blockedByProximity) {
    const help = locationHelpSteps();
    const needsPermission = proximity.reason === 'no_captured_location';
    const isRequesting = geolocationStatus === 'requesting';
    const isHardDenied = geolocationStatus === 'denied_hard';
    const isInsecure = geolocationStatus === 'insecure_context';
    const isUnsupported = geolocationStatus === 'unsupported';

    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-green-50 to-white flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-lg shadow-xl">
          <CardContent className="pt-8 pb-8 px-6">
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <MapPin className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Only on this location this form can be opened and submitted
              </h2>
              <p className="text-sm text-gray-600">
                This verification must be completed at the address being verified.
              </p>
            </div>

            {caseData?.presentAddress && (
              <div className="bg-gray-50 border rounded-lg p-3 mb-5">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Address to verify</p>
                <p className="text-sm text-gray-800">{caseData.presentAddress}</p>
              </div>
            )}

            {/* Out of radius: we know where they are, and it is not here. */}
            {!needsPermission && proximity.reason === 'outside_radius' && (
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-5">
                <p className="text-sm text-red-800">
                  You are currently about{' '}
                  <span className="font-bold">
                    {proximity.distanceMeters !== null && proximity.distanceMeters >= 1000
                      ? `${(proximity.distanceMeters / 1000).toFixed(1)}km`
                      : `${proximity.distanceMeters}m`}
                  </span>{' '}
                  away. Please go to the address above and reopen this page.
                </p>
                {proximity.accuracyExceedsRadius && (
                  <p className="text-xs text-red-700 mt-2">
                    Your device's location signal is weak right now. Step outside or near a
                    window and tap Check My Location again.
                  </p>
                )}
              </div>
            )}

            {/* Permission missing: location is mandatory to open the form. */}
            {needsPermission && !isInsecure && !isUnsupported && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4 mb-5">
                <p className="text-sm text-blue-900 font-medium mb-1">Location access is required</p>
                <p className="text-sm text-blue-800">
                  We need your location to confirm you are at the address. This form cannot be
                  opened without it.
                </p>
              </div>
            )}

            {/* Hard denial: per-platform recovery steps (iOS/Android/Windows/Mac). */}
            {isHardDenied && (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4 mb-5">
                <p className="text-sm font-semibold text-yellow-900 mb-2">{help.title}</p>
                <ol className="list-decimal list-inside space-y-1 text-sm text-yellow-900">
                  {help.steps.map((step, i) => <li key={i}>{step}</li>)}
                </ol>
              </div>
            )}

            {isInsecure && (
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-5">
                <p className="text-sm text-red-800">
                  This page must be opened over a secure (<span className="font-mono">https://</span>)
                  link. Please reopen the link from your email or SMS.
                </p>
              </div>
            )}

            {isUnsupported && (
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 mb-5">
                <p className="text-sm text-red-800">
                  This browser cannot share your location. Please open this link in Chrome
                  (Android/Windows) or Safari (iPhone/Mac).
                </p>
              </div>
            )}

            {/* The retry button. Present on every platform and every failure
                state that a retry can clear -- this is the "allow it again"
                affordance for a candidate who denied permission. */}
            {!isInsecure && !isUnsupported && (
              <Button
                type="button"
                onClick={captureGeolocation}
                disabled={isRequesting}
                className="w-full bg-brand-green hover:bg-brand-green-600 text-white"
              >
                {isRequesting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Checking your location…</>
                ) : (
                  <><MapPin className="w-4 h-4 mr-2" /> {needsPermission ? 'Allow Location Access' : 'Check My Location'}</>
                )}
              </Button>
            )}

            {!isHardDenied && !isInsecure && !isUnsupported && (
              <p className="text-xs text-gray-500 text-center mt-4">
                When your browser asks, choose <span className="font-semibold">Allow</span>.
              </p>
            )}

            <div className="mt-6 pt-4 border-t text-center">
              <p className="text-xs text-gray-500">
                Need help? Call +91 7982938489 / +91 9871967859
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-green-50 to-white">
      {/* Professional Header */}
      <div className="bg-white shadow-md border-b-4 border-brand-green">
        <div className="max-w-6xl mx-auto px-4 py-3 sm:py-6">
          <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-2 sm:gap-4">
            <img src="/logo.jpg" alt="Zella Screenings" className="h-10 sm:h-16 object-contain" />
            <div className="flex flex-col sm:items-end items-center text-center sm:text-right">
              <a href="mailto:start@zellascreenings.com" className="text-xs sm:text-sm text-gray-600 flex items-center gap-1.5 sm:gap-2 hover:text-brand-green">
                <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="break-all">start@zellascreenings.com</span>
              </a>
              <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-1.5 sm:gap-2 mt-0.5 sm:mt-1">
                <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>+91 7982938489 / +91 9871967859</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-8">
        {/* Title */}
        <div className="text-center mb-4 sm:mb-8">
          <h1 className="text-xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">Address Verification</h1>
          <p className="text-sm sm:text-base text-gray-600">Complete your background verification process</p>
        </div>

        {/* Stepper */}
        <div className="mb-6 sm:mb-8">
          <Stepper steps={steps} currentStep={currentStep} />
        </div>

        {/* Pre-filled Case Data Card */}
        <Card className="mb-6 shadow-lg border-l-4 border-brand-green">
          <CardHeader className="bg-gradient-to-r from-brand-green-50 to-white">
            <CardTitle className="text-brand-green">Candidate Information</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-gray-600 text-sm font-semibold">Code</Label>
                <div className="mt-1 p-3 bg-gray-50 rounded-lg border border-gray-200 font-medium">{caseData?.code}</div>
              </div>
              <div>
                <Label className="text-gray-600 text-sm font-semibold">Applicant Name</Label>
                <div className="mt-1 p-3 bg-gray-50 rounded-lg border border-gray-200 font-medium">{caseData?.name}</div>
              </div>
              <div>
                <Label className="text-gray-600 text-sm font-semibold">Applicant No</Label>
                <div className="mt-1 p-3 bg-gray-50 rounded-lg border border-gray-200 font-medium">{caseData?.applicantNo}</div>
              </div>
              <div>
                <Label className="text-gray-600 text-sm font-semibold">Father's Name</Label>
                <div className="mt-1 p-3 bg-gray-50 rounded-lg border border-gray-200 font-medium">{caseData?.fathersName || 'N/A'}</div>
              </div>
              <div>
                <Label className="text-gray-600 text-sm font-semibold">Mobile No</Label>
                <div className="mt-1 p-3 bg-gray-50 rounded-lg border border-gray-200 font-medium">{caseData?.phone}</div>
              </div>
              <div>
                <Label className="text-gray-600 text-sm font-semibold">Date</Label>
                <div className="mt-1 p-3 bg-gray-50 rounded-lg border border-gray-200 font-medium">
                  {caseData?.formSubmitDate ? new Date(caseData.formSubmitDate).toLocaleDateString() : 'N/A'}
                </div>
              </div>
              <div className="md:col-span-3">
                <Label className="text-gray-600 text-sm font-semibold">Present Address</Label>
                <div className="mt-1 p-3 bg-gray-50 rounded-lg border border-gray-200 font-medium">{caseData?.presentAddress}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Form Card */}
        <Card className="shadow-xl">
          <CardContent className="px-3 sm:px-6 pt-4 sm:pt-6 pb-4 sm:pb-6">
            <form onSubmit={handleSubmit}>
              {/* Step 1: Personal Information */}
              {currentStep === 1 && (
                <div className="space-y-8">
                  {/* Contact Information */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b-2 border-brand-green">
                      <div className="w-8 h-8 bg-brand-green rounded-full flex items-center justify-center text-white font-bold">1</div>
                      <h3 className="text-xl font-semibold text-gray-900">Contact Information</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contactPersonName" className="text-gray-700 font-medium">
                          Contact Person Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="contactPersonName"
                          value={formData.contactPersonName}
                          onChange={(e) => handleInputChange('contactPersonName', e.target.value)}
                          placeholder="Enter contact person name"
                          className="mt-1"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="contactPersonRelation" className="text-gray-700 font-medium">
                          Contact Person Relation <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="contactPersonRelation"
                          value={formData.contactPersonRelation}
                          onChange={(e) => handleInputChange('contactPersonRelation', e.target.value)}
                          placeholder="e.g., Father, Mother, Spouse"
                          className="mt-1"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="numberOfFamilyMembers" className="text-gray-700 font-medium">No of Family Members</Label>
                        <Input
                          id="numberOfFamilyMembers"
                          type="number"
                          min="1"
                          value={formData.numberOfFamilyMembers}
                          onChange={(e) => handleInputChange('numberOfFamilyMembers', e.target.value)}
                          placeholder="Enter number"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="contactPhoneNo" className="text-gray-700 font-medium">Contact Person Phone No</Label>
                        <Input
                          id="contactPhoneNo"
                          type="tel"
                          value={formData.contactPhoneNo}
                          onChange={(e) => handleInputChange('contactPhoneNo', e.target.value)}
                          placeholder="Enter 10-digit mobile number"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* ID Proof */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b-2 border-brand-green">
                      <div className="w-8 h-8 bg-brand-green rounded-full flex items-center justify-center text-white font-bold">2</div>
                      <h3 className="text-xl font-semibold text-gray-900">ID Proof</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="idProofType" className="text-gray-700 font-medium">
                          ID Proof Type <span className="text-red-500">*</span>
                        </Label>
                        <select
                          id="idProofType"
                          value={formData.idProofType}
                          onChange={(e) => handleInputChange('idProofType', e.target.value)}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green"
                          required
                        >
                          <option value="">Select ID Proof Type</option>
                          <option value="aadhaar">Aadhaar Card</option>
                          <option value="pan">PAN Card</option>
                          <option value="voter_id">Voter ID</option>
                          <option value="passport">Passport</option>
                          <option value="driving_license">Driving License</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div>
                        <Label htmlFor="idProofNumber" className="text-gray-700 font-medium">ID Proof Number</Label>
                        <Input
                          id="idProofNumber"
                          value={formData.idProofNumber}
                          onChange={(e) => handleInputChange('idProofNumber', e.target.value)}
                          placeholder="Enter ID proof number"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Address Details */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b-2 border-brand-green">
                      <div className="w-8 h-8 bg-brand-green rounded-full flex items-center justify-center text-white font-bold">3</div>
                      <h3 className="text-xl font-semibold text-gray-900">Address Details</h3>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="periodOfStay" className="text-gray-700 font-medium">Period of Stay</Label>
                        <Input
                          id="periodOfStay"
                          value={formData.periodOfStay}
                          onChange={(e) => handleInputChange('periodOfStay', e.target.value)}
                          placeholder="e.g., 2 years"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="differentAddress" className="text-gray-700 font-medium">If different address</Label>
                        <Input
                          id="differentAddress"
                          value={formData.differentAddress}
                          onChange={(e) => handleInputChange('differentAddress', e.target.value)}
                          placeholder="Enter different address if applicable"
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label htmlFor="residentialStatus" className="text-gray-700 font-medium">
                          Residential Status <span className="text-red-500">*</span>
                        </Label>
                        <select
                          id="residentialStatus"
                          value={formData.residentialStatus}
                          onChange={(e) => handleInputChange('residentialStatus', e.target.value)}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green"
                          required
                        >
                          <option value="">Select Residential Status</option>
                          <option value="owned">Owned</option>
                          <option value="rented">Rented</option>
                          <option value="company_provided">Company Provided</option>
                          <option value="pg">PG/Hostel</option>
                          <option value="other">Other</option>
                        </select>
                      </div>

                      <div>
                        <Label htmlFor="landmark" className="text-gray-700 font-medium">Landmark</Label>
                        <Input
                          id="landmark"
                          value={formData.landmark}
                          onChange={(e) => handleInputChange('landmark', e.target.value)}
                          placeholder="Enter nearby landmark"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="remarks" className="text-gray-700 font-medium">Remarks</Label>
                      <textarea
                        id="remarks"
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-green"
                        rows={3}
                        value={formData.remarks}
                        onChange={(e) => handleInputChange('remarks', e.target.value)}
                        placeholder="Any additional remarks"
                      />
                    </div>
                  </div>

                  {/* Geolocation Status */}
                  <div>
                    {geolocationStatus === 'granted' && formData.latitude !== undefined && formData.longitude !== undefined && (
                      <div className="bg-brand-green-50 p-4 rounded-lg border-2 border-brand-green-200">
                        <p className="text-sm text-brand-green-800 font-medium flex items-center gap-2">
                          <CheckCircle className="w-5 h-5" />
                          Location captured: Latitude {formData.latitude.toFixed(6)}, Longitude {formData.longitude.toFixed(6)}
                        </p>
                      </div>
                    )}

                    {geolocationStatus === 'idle' && (
                      <div className="bg-blue-50 p-6 rounded-lg border-2 border-blue-200">
                        <div className="flex items-start gap-3">
                          <MapPin className="w-6 h-6 text-blue-600 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-blue-900 font-semibold mb-1">Share your location</h4>
                            <p className="text-sm text-blue-800 mb-3">
                              We need your current location to verify the address. Tap below — your browser will ask for permission.
                            </p>
                            <Button
                              type="button"
                              onClick={captureGeolocation}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              size="sm"
                            >
                              <MapPin className="w-4 h-4 mr-1.5" />
                              Enable Location
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {geolocationStatus === 'requesting' && (
                      <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-200">
                        <p className="text-sm text-blue-800 font-medium flex items-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Waiting for location… If your phone asks for permission, tap <span className="font-bold">Allow</span>. This may take up to 30 seconds.
                        </p>
                      </div>
                    )}

                    {geolocationStatus === 'denied_soft' && (
                      <div className="bg-yellow-50 p-6 rounded-lg border-2 border-yellow-300">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-6 h-6 text-yellow-600 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-yellow-900 font-semibold mb-1">Permission was dismissed</h4>
                            <p className="text-sm text-yellow-800 mb-3">
                              We didn't get location permission. Tap <span className="font-bold">Try Again</span> and choose <span className="font-bold">Allow</span> when your browser asks.
                            </p>
                            <Button
                              type="button"
                              onClick={captureGeolocation}
                              className="bg-yellow-600 hover:bg-yellow-700 text-white"
                              size="sm"
                            >
                              Try Again
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {geolocationStatus === 'denied_hard' && (
                      <div className="bg-red-50 p-6 rounded-lg border-2 border-red-300">
                        <div className="flex items-start gap-3">
                          <ShieldAlert className="w-6 h-6 text-red-600 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-red-900 font-semibold mb-1">Location is blocked for this site</h4>
                            {getMobileOS() === 'ios' ? (
                              <div className="text-sm text-red-800 mb-3 space-y-2">
                                <p className="font-medium">To allow location on iPhone/iPad:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                  <li>Open the <span className="font-semibold">Settings</span> app.</li>
                                  <li>Go to <span className="font-semibold">Privacy &amp; Security</span> → <span className="font-semibold">Location Services</span> and make sure it's <span className="font-semibold">On</span>.</li>
                                  <li>Scroll down, tap <span className="font-semibold">Safari Websites</span>, and choose <span className="font-semibold">Ask</span> or <span className="font-semibold">Allow</span>.</li>
                                  <li>Return to this page and tap <span className="font-semibold">Try Again</span>.</li>
                                </ol>
                              </div>
                            ) : getMobileOS() === 'android' ? (
                              <div className="text-sm text-red-800 mb-3 space-y-2">
                                <p className="font-medium">To allow location on Android Chrome:</p>
                                <ol className="list-decimal list-inside space-y-1">
                                  <li>Tap the <span className="font-semibold">lock icon</span> (or ⓘ) in the address bar.</li>
                                  <li>Tap <span className="font-semibold">Permissions</span> and set <span className="font-semibold">Location</span> to <span className="font-semibold">Allow</span>.</li>
                                  <li>Also ensure your phone's Location is on (Settings → Location).</li>
                                  <li>Return here and tap <span className="font-semibold">Try Again</span>.</li>
                                </ol>
                              </div>
                            ) : (
                              <p className="text-sm text-red-800 mb-3">
                                Open your browser's site settings for this page and set Location to <span className="font-semibold">Allow</span>, then tap Try Again.
                              </p>
                            )}
                            <Button
                              type="button"
                              onClick={captureGeolocation}
                              className="bg-red-600 hover:bg-red-700 text-white"
                              size="sm"
                            >
                              Try Again
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {geolocationStatus === 'timeout' && (
                      <div className="bg-yellow-50 p-6 rounded-lg border-2 border-yellow-300">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-6 h-6 text-yellow-600 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-yellow-900 font-semibold mb-1">Couldn't get a precise location</h4>
                            <p className="text-sm text-yellow-800 mb-3">
                              Your phone is taking too long to find a GPS signal. Step outside or near a window, then tap Try Again.
                            </p>
                            <Button
                              type="button"
                              onClick={captureGeolocation}
                              className="bg-yellow-600 hover:bg-yellow-700 text-white"
                              size="sm"
                            >
                              Try Again
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {geolocationStatus === 'unavailable' && (
                      <div className="bg-yellow-50 p-6 rounded-lg border-2 border-yellow-300">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-6 h-6 text-yellow-600 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-yellow-900 font-semibold mb-1">Location services are off</h4>
                            <p className="text-sm text-yellow-800 mb-3">
                              {getMobileOS() === 'ios'
                                ? 'Turn on Location Services in Settings → Privacy & Security → Location Services, then come back and tap Try Again.'
                                : getMobileOS() === 'android'
                                ? 'Turn on Location in your phone\'s settings (Settings → Location), then come back and tap Try Again.'
                                : 'Turn on Location/GPS in your device\'s settings, then come back and tap Try Again.'}
                            </p>
                            <Button
                              type="button"
                              onClick={captureGeolocation}
                              className="bg-yellow-600 hover:bg-yellow-700 text-white"
                              size="sm"
                            >
                              Try Again
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {geolocationStatus === 'insecure_context' && (
                      <div className="bg-red-50 p-6 rounded-lg border-2 border-red-300">
                        <div className="flex items-start gap-3">
                          <ShieldAlert className="w-6 h-6 text-red-600 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-red-900 font-semibold mb-1">Insecure connection</h4>
                            <p className="text-sm text-red-800">
                              This page must be opened over a secure (<span className="font-mono">https://</span>) link. Please check the link in your email and reopen it.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {geolocationStatus === 'unsupported' && (
                      <div className="bg-red-50 p-6 rounded-lg border-2 border-red-300">
                        <div className="flex items-start gap-3">
                          <ShieldAlert className="w-6 h-6 text-red-600 mt-0.5 shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-red-900 font-semibold mb-1">Browser doesn't support location</h4>
                            <p className="text-sm text-red-800">
                              Please open this link in Chrome (Android) or Safari (iOS).
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Navigation */}
                  <div className="flex justify-end pt-6">
                    <Button
                      type="button"
                      onClick={handleNext}
                      disabled={!isStep1Valid}
                      className="bg-brand-green hover:bg-brand-green-600 text-white px-4 sm:px-8 disabled:opacity-50 disabled:cursor-not-allowed"
                      size="lg"
                    >
                      Next Step <ChevronRight className="ml-2 w-5 h-5" />
                    </Button>
                  </div>
                  {!isStep1Valid && (
                    <p className="text-sm text-gray-500 text-right mt-2">
                      {geolocationStatus !== 'granted'
                        ? 'Tap Enable Location above to capture your location, then complete the form to continue.'
                        : 'Please complete all required fields to proceed.'}
                    </p>
                  )}
                </div>
              )}

              {/* Step 2: Document Upload */}
              {currentStep === 2 && (
                <div className="space-y-8">
                  <div className="space-y-4">
                    <div className="flex items-center gap-2 pb-2 border-b-2 border-brand-green">
                      <div className="w-8 h-8 bg-brand-green rounded-full flex items-center justify-center text-white font-bold">4</div>
                      <h3 className="text-xl font-semibold text-gray-900">Document Uploads</h3>
                    </div>
                    <p className="text-sm text-gray-600">All documents are required (max 15MB each, PDF/JPG/PNG only)</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* ID Proof One */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-brand-green transition-colors">
                        <Label htmlFor="idProofOne" className="text-gray-700 font-medium">
                          ID Proof One <span className="text-red-500">*</span>
                        </Label>
                        <input
                          type="file"
                          id="idProofOne"
                          accept="image/*,.heic,.heif,application/pdf,.pdf"
                          onChange={(e) => handleFileChange('idProofOne', e.target.files?.[0] || null)}
                          className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-brand-green-50 file:text-brand-green hover:file:bg-brand-green-100"
                          required
                        />
                        {documents.idProofOne && (
                          <p className="text-xs text-brand-green font-medium mt-2">✓ {documents.idProofOne.name}</p>
                        )}
                        {fileErrors['idProofOne'] && (
                          <p className="text-xs text-red-600 font-medium mt-2">{fileErrors['idProofOne']}</p>
                        )}
                      </div>

                      {/* ID Proof Two */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-brand-green transition-colors">
                        <Label htmlFor="idProofTwo" className="text-gray-700 font-medium">
                          ID Proof Two <span className="text-red-500">*</span>
                        </Label>
                        <input
                          type="file"
                          id="idProofTwo"
                          accept="image/*,.heic,.heif,application/pdf,.pdf"
                          onChange={(e) => handleFileChange('idProofTwo', e.target.files?.[0] || null)}
                          className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-brand-green-50 file:text-brand-green hover:file:bg-brand-green-100"
                          required
                        />
                        {documents.idProofTwo && (
                          <p className="text-xs text-brand-green font-medium mt-2">✓ {documents.idProofTwo.name}</p>
                        )}
                        {fileErrors['idProofTwo'] && (
                          <p className="text-xs text-red-600 font-medium mt-2">{fileErrors['idProofTwo']}</p>
                        )}
                      </div>

                      {/* House Image One */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-brand-green transition-colors">
                        <Label htmlFor="houseImageOne" className="text-gray-700 font-medium">
                          House Image One <span className="text-red-500">*</span>
                          <span className="block text-xs font-normal text-gray-500">Take a photo now — gallery uploads are not accepted</span>
                        </Label>
                        <input
                          type="file"
                          id="houseImageOne"
                          accept="image/*" capture="environment"
                          onChange={(e) => handleFileChange('houseImageOne', e.target.files?.[0] || null)}
                          className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-brand-green-50 file:text-brand-green hover:file:bg-brand-green-100"
                          required
                        />
                        {documents.houseImageOne && (
                          <p className="text-xs text-brand-green font-medium mt-2">✓ {documents.houseImageOne.name}</p>
                        )}
                        {fileErrors['houseImageOne'] && (
                          <p className="text-xs text-red-600 font-medium mt-2">{fileErrors['houseImageOne']}</p>
                        )}
                      </div>

                      {/* House Image Two */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-brand-green transition-colors">
                        <Label htmlFor="houseImageTwo" className="text-gray-700 font-medium">
                          House Image Two <span className="text-red-500">*</span>
                          <span className="block text-xs font-normal text-gray-500">Take a photo now — gallery uploads are not accepted</span>
                        </Label>
                        <input
                          type="file"
                          id="houseImageTwo"
                          accept="image/*" capture="environment"
                          onChange={(e) => handleFileChange('houseImageTwo', e.target.files?.[0] || null)}
                          className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-brand-green-50 file:text-brand-green hover:file:bg-brand-green-100"
                          required
                        />
                        {documents.houseImageTwo && (
                          <p className="text-xs text-brand-green font-medium mt-2">✓ {documents.houseImageTwo.name}</p>
                        )}
                        {fileErrors['houseImageTwo'] && (
                          <p className="text-xs text-red-600 font-medium mt-2">{fileErrors['houseImageTwo']}</p>
                        )}
                      </div>

                      {/* Signature */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-brand-green transition-colors">
                        <Label htmlFor="signature" className="text-gray-700 font-medium">
                          Signature <span className="text-red-500">*</span>
                          <span className="block text-xs font-normal text-gray-500">Take a photo now — gallery uploads are not accepted</span>
                        </Label>
                        <input
                          type="file"
                          id="signature"
                          accept="image/*" capture="environment"
                          onChange={(e) => handleFileChange('signature', e.target.files?.[0] || null)}
                          className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-brand-green-50 file:text-brand-green hover:file:bg-brand-green-100"
                          required
                        />
                        {documents.signature && (
                          <p className="text-xs text-brand-green font-medium mt-2">✓ {documents.signature.name}</p>
                        )}
                        {fileErrors['signature'] && (
                          <p className="text-xs text-red-600 font-medium mt-2">{fileErrors['signature']}</p>
                        )}
                      </div>

                      {/* Selfie */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-brand-green transition-colors">
                        <Label htmlFor="selfie" className="text-gray-700 font-medium">
                          Selfie (Candidate Image) <span className="text-red-500">*</span>
                          <span className="block text-xs font-normal text-gray-500">Take a photo now — gallery uploads are not accepted</span>
                        </Label>
                        <input
                          type="file"
                          id="selfie"
                          accept="image/*" capture="user"
                          onChange={(e) => handleFileChange('selfie', e.target.files?.[0] || null)}
                          className="mt-2 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-brand-green-50 file:text-brand-green hover:file:bg-brand-green-100"
                          required
                        />
                        {documents.selfie && (
                          <p className="text-xs text-brand-green font-medium mt-2">✓ {documents.selfie.name}</p>
                        )}
                        {fileErrors['selfie'] && (
                          <p className="text-xs text-red-600 font-medium mt-2">{fileErrors['selfie']}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Navigation */}
                  <div className="flex justify-between pt-6">
                    <Button
                      type="button"
                      onClick={handleBack}
                      variant="outline"
                      size="lg"
                      className="border-brand-green text-brand-green hover:bg-brand-green-50"
                    >
                      <ChevronLeft className="mr-2 w-5 h-5" /> Previous Step
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="bg-brand-green hover:bg-brand-green-600 text-white px-4 sm:px-8"
                      size="lg"
                    >
                      {submitting ? (uploadProgress || 'Submitting...') : 'Submit Verification'}
                    </Button>
                  </div>

                  {uploadProgress && (
                    <p className="mt-3 text-sm text-gray-600 text-center">{uploadProgress}</p>
                  )}

                  {submitError && (
                    <div className="mt-4 p-3 border border-red-300 bg-red-50 rounded-lg">
                      <p className="text-sm text-red-800 whitespace-pre-line">{submitError}</p>
                    </div>
                  )}
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 sm:mt-8 text-sm text-gray-500 bg-white p-4 sm:p-6 rounded-lg shadow">
          <p className="font-medium text-gray-700 mb-2">Need Help?</p>
          <p className="break-words">
            Contact us at{' '}
            <a href="mailto:start@zellascreenings.com" className="text-brand-green hover:underline font-medium">
              start@zellascreenings.com
            </a>
          </p>
          <p className="mt-1 break-words">
            Phone:{' '}
            <a href="tel:+917982938489" className="hover:text-brand-green">+91 7982938489</a>
            {' '}/{' '}
            <a href="tel:+919871967859" className="hover:text-brand-green">+91 9871967859</a>
          </p>
          <p className="mt-4 text-xs text-gray-400">
            © {new Date().getFullYear()} Zella Screenings. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AddressVerificationPage;
